import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { designTokens } from '@/utils/design-tokens'
import Button from '@/components/ui/Button'
import { Settings } from 'lucide-react'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { AcademyListItem } from '@/api/generated/models/AcademyListItem'
import type { CompareResponse } from '@/api/generated/models/CompareResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { formatIndicatorValue } from '../types'
import YearDetailModal from './YearDetailModal'

interface SchoolComparisonTabProps {
  orgId: string
  myAcademy: AcademyListItem
  compareAcademies: AcademyListItem[]
  selectedYear: string
  availableYears: string[]
  onYearChange: (year: string) => void
  onOpenSettings: () => void
  tabSwitcher?: React.ReactNode
}

const SchoolComparisonTab: React.FC<SchoolComparisonTabProps> = ({
  orgId,
  myAcademy,
  compareAcademies,
  selectedYear,
  availableYears,
  onYearChange,
  onOpenSettings,
  tabSwitcher,
}) => {
  const { showError } = useAlert()
  const [compareData, setCompareData] = useState<CompareResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [detailIndicatorId, setDetailIndicatorId] = useState<string | null>(null)
  const [detailIndicatorName, setDetailIndicatorName] = useState('')

  const allAcademyIds = [myAcademy.id, ...compareAcademies.map(a => a.id)]

  const fetchCompareData = useCallback(async () => {
    if (!selectedYear || !orgId || allAcademyIds.length === 0) return
    setIsLoading(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsCompareAcademies({
        academyIds: allAcademyIds.join(','),
        surveyYear: selectedYear,
        orgId,
      })
      setCompareData(response)
    } catch (err: any) {
      console.error('Failed to fetch compare data:', err)
      // 기관에서 비공개 처리한 연도(hidden_years)에 진입한 경우 백엔드는 404 + '비공개' 메시지 반환
      const status = err?.status
      const errorMsg = err?.body?.error?.message || err?.body?.error || err?.body?.detail
      if (status === 404 && typeof errorMsg === 'string' && errorMsg.includes('비공개')) {
        showError('이 연도는 현재 기관에서 비공개로 설정되어 있습니다.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [selectedYear, orgId, myAcademy.id, compareAcademies, showError])

  useEffect(() => {
    fetchCompareData()
  }, [fetchCompareData])

  // 지표를 가나다 순으로 정렬 (한글 로케일 기준)
  const sortedIndicators = useMemo(() => {
    if (!compareData?.indicators) return []
    return [...compareData.indicators].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'ko')
    )
  }, [compareData])

  const handleOpenDetail = (indicatorMasterId: string, indicatorName: string) => {
    setDetailIndicatorId(indicatorMasterId)
    setDetailIndicatorName(indicatorName)
  }

  const cellStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: '14px',
    color: designTokens.colors.gray[6],
    borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
    whiteSpace: 'nowrap',
  }

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    fontWeight: 600,
    fontSize: '13px',
    backgroundColor: designTokens.colors.gray[0],
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  }

  return (
    <div className="h-full flex flex-col">
      {/* Info Bar */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          {tabSwitcher}
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '16px',
              fontWeight: 600,
              color: designTokens.colors.gray[6],
            }}
          >
            우리학교: {myAcademy.name}
          </span>
          {myAcademy.establishment_type && (
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', color: designTokens.colors.gray[4] }}>
              ({myAcademy.establishment_type} / {myAcademy.region})
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Year Selector */}
          {availableYears.length > 0 && (
            <div
              className="flex items-center rounded overflow-hidden"
              style={{ border: `1px solid ${designTokens.colors.gray[2]}` }}
            >
              {availableYears.map((year, idx) => (
                <button
                  key={year}
                  onClick={() => onYearChange(year)}
                  style={{
                    padding: '4px 14px',
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '13px',
                    fontWeight: selectedYear === year ? 600 : 400,
                    color: selectedYear === year ? '#fff' : designTokens.colors.gray[5],
                    backgroundColor: selectedYear === year ? designTokens.colors.primary.DEFAULT : '#fff',
                    border: 'none',
                    borderRight: idx < availableYears.length - 1 ? `1px solid ${designTokens.colors.gray[2]}` : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          <Button size="small" color="white" onClick={onOpenSettings} className="flex items-center gap-1">
            <Settings className="w-3.5 h-3.5" />
            비교학교 변경
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto rounded border" style={{ borderColor: designTokens.colors.gray[1] }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
              데이터를 불러오는 중...
            </span>
          </div>
        ) : !compareData || compareData.indicators.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
              비교 데이터가 없습니다.
            </span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, width: '220px', textAlign: 'left', position: 'sticky', left: 0, zIndex: 2 }}>
                  지표
                </th>
                <th style={{ ...headerCellStyle, textAlign: 'right', backgroundColor: designTokens.colors.secondary.light + '30' }}>
                  {myAcademy.name}
                </th>
                {compareAcademies.map(school => (
                  <th key={school.id} style={{ ...headerCellStyle, textAlign: 'right' }}>
                    {school.name}
                  </th>
                ))}
                <th style={{ ...headerCellStyle, width: '60px', textAlign: 'center' }}>
                  단위
                </th>
                <th style={{ ...headerCellStyle, width: '80px', textAlign: 'center' }}>
                  상세보기
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedIndicators.map(indicator => {
                const myValue = indicator.values?.[myAcademy.id]?.value ?? null

                return (
                  <tr key={indicator.indicator_master_id} className="hover:bg-gray-50">
                    <td style={{ ...cellStyle, fontWeight: 500, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                      {indicator.name}
                    </td>

                    {/* My School */}
                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600, backgroundColor: designTokens.colors.secondary.light + '15' }}>
                      {formatIndicatorValue(myValue, indicator.unit, { raw: true })}
                    </td>

                    {/* Comparison Schools */}
                    {compareAcademies.map(school => {
                      const val = indicator.values?.[school.id]?.value ?? null
                      return (
                        <td key={school.id} style={{ ...cellStyle, textAlign: 'right' }}>
                          {formatIndicatorValue(val, indicator.unit, { raw: true })}
                        </td>
                      )
                    })}

                    {/* Unit */}
                    <td style={{ ...cellStyle, textAlign: 'center', color: designTokens.colors.gray[4], fontSize: '12px' }}>
                      {indicator.unit || '-'}
                    </td>

                    {/* Detail */}
                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                      <button
                        onClick={() => handleOpenDetail(indicator.indicator_master_id, indicator.name)}
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          color: designTokens.colors.secondary.dark,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        상세보기
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Year Detail Modal */}
      {detailIndicatorId && (
        <YearDetailModal
          isOpen={!!detailIndicatorId}
          onClose={() => setDetailIndicatorId(null)}
          orgId={orgId}
          indicatorMasterId={detailIndicatorId}
          indicatorName={detailIndicatorName}
          academyIds={allAcademyIds}
          myAcademyId={myAcademy.id}
          myAcademyRegion={myAcademy.region}
          availableYears={availableYears}
        />
      )}
    </div>
  )
}

export default SchoolComparisonTab
